/**
 * Colour maths shared by the swatch PNG encoder and the admin UI, so a chip
 * looks the same in the CMS as it does in the app.
 */
/** `#rrggbb` or `rrggbb` → `[r, g, b]`. */
export function parseHex(hex) {
    const clean = hex.trim().replace(/^#/, '');
    if (!/^[0-9a-fA-F]{6}$/.test(clean))
        throw new Error(`Not a six-digit hex colour: ${hex}`);
    return [
        parseInt(clean.slice(0, 2), 16),
        parseInt(clean.slice(2, 4), 16),
        parseInt(clean.slice(4, 6), 16),
    ];
}
export function toHex([r, g, b]) {
    return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}
/** Perceived brightness, 0–1. Green dominates because human vision does. */
export function luminance([r, g, b]) {
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}
const EDGE_BLEND = 0.26;
/**
 * Below this brightness the edge lightens instead of darkening.
 *
 * Set well under the midpoint on purpose: most surfaces a chip sits on are
 * light, and a darker edge reads as a border there. Only genuinely dark paints
 * — near-blacks and deep navies — need the opposite treatment, and those are
 * the ones that would vanish on a dark theme.
 */
const DARK_FILL_THRESHOLD = 0.25;
/**
 * A border colour for a swatch of `hex`.
 *
 * Derived from the fill rather than fixed, because the problem case is a chip
 * whose background matches it — white paint on a white card, or black on a dark
 * theme. A fixed grey border fails one of those; blending the fill toward its
 * *opposite* extreme keeps the edge visible against any background, including
 * one identical to the fill.
 *
 * Blending rather than using flat black or white keeps it colour-aware: a red
 * swatch gets a deeper red edge, not a grey one.
 */
export function swatchEdgeColor(hex) {
    const rgb = parseHex(hex);
    const target = luminance(rgb) > DARK_FILL_THRESHOLD ? 0 : 255;
    return toHex(rgb.map((c) => c + (target - c) * EDGE_BLEND));
}
//# sourceMappingURL=color.js.map